import React, { createContext, useContext, useState, useEffect } from 'react';
import useAxiosClient from '../components/axiosClient';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const axiosClient = useAxiosClient();

  // Load locations from server
  const loadLocations = async () => {
    try {
      setIsLoading(true);
      
      // FIX: Use correct endpoint /user-locations
      const response = await axiosClient.get('/user-locations');
      
      if (response.data.success && response.data.locations && response.data.locations.length > 0) {
        // Map server data to frontend structure
        const serverLocations = response.data.locations.map(location => ({
          id: location.id,
          name: location.name,
          address: location.address || '',
          city: location.city || '',
          state: location.state || '',
          // Handle DB column name variations safely
          zipCode: location.zipCode || location.zip_code || '', 
          country: location.country || 'United States',
          phone: location.phone || '',
          email: location.email || '',
          isDefault: location.is_default || false
        }));
        
        setLocations(serverLocations);
        
        // Logic to determine current selected location
        const savedCurrentLocationId = localStorage.getItem('currentLocationId');
        let currentLoc = null;
        
        if (savedCurrentLocationId) {
          currentLoc = serverLocations.find(loc => loc.id.toString() === savedCurrentLocationId);
        }
        
        if (!currentLoc) {
          // Find default location or use first one
          currentLoc = serverLocations.find(loc => loc.isDefault) || serverLocations[0];
        }
        
        setCurrentLocation(currentLoc);
        if (currentLoc) {
            localStorage.setItem('currentLocationId', currentLoc.id.toString());
        }
      } else {
        // No locations found?
        setLocations([]);
        setCurrentLocation(null);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setIsLoading(false);
    }
  };

  // Add new location
  const addLocation = async (locationData) => {
    try {
      // FIX: Use correct endpoint and variable names (zipCode)
      const response = await axiosClient.post('/user-locations', {
        name: locationData.name,
        address: locationData.address,
        city: locationData.city,
        state: locationData.state,
        zipCode: locationData.zipCode, // Matches Controller validation
        country: locationData.country,
        phone: locationData.phone,
        email: locationData.email
      });

      if (response.data.success) {
        // Construct new location object from response
        const newLocation = {
          id: response.data.location.id,
          name: response.data.location.name,
          address: response.data.location.address,
          city: response.data.location.city,
          state: response.data.location.state,
          zipCode: response.data.location.zipCode,
          country: response.data.location.country,
          phone: response.data.location.phone,
          email: response.data.location.email,
          isDefault: response.data.location.is_default || false
        };
        
        const updatedLocations = [...locations, newLocation];
        setLocations(updatedLocations);
        
        // If this is the first location, set it as current
        if (locations.length === 0) {
          setCurrentLocation(newLocation);
          localStorage.setItem('currentLocationId', newLocation.id.toString());
        }
        
        return newLocation;
      } else {
        throw new Error(response.data.message || 'Failed to create location');
      }
    } catch (error) {
      throw error;
    }
  };

  // Update location
  const updateLocation = async (locationId, locationData) => {
    try {
      // FIX: Use PUT and correct endpoint
      const response = await axiosClient.put(`/user-locations/${locationId}`, {
        name: locationData.name,
        address: locationData.address,
        city: locationData.city,
        state: locationData.state,
        zipCode: locationData.zipCode, // Matches Controller
        country: locationData.country,
        phone: locationData.phone,
        email: locationData.email
      });

      if (response.data.success) {
        const updatedLocations = locations.map(loc => 
          loc.id === locationId ? { ...loc, ...locationData } : loc
        );
        setLocations(updatedLocations);
        
        // Update current location if it was the one being updated
        if (currentLocation && currentLocation.id === locationId) {
          const updatedCurrentLocation = { ...currentLocation, ...locationData };
          setCurrentLocation(updatedCurrentLocation);
        }
        
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to update location');
      }
    } catch (error) {
      throw error;
    }
  };

  // Delete location
  const deleteLocation = async (locationId) => {
    if (locations.length <= 1) {
      alert('Cannot delete the last location. You must have at least one location.');
      return false;
    }
    
    try {
      // FIX: Use correct endpoint
      const response = await axiosClient.delete(`/user-locations/${locationId}`);

      if (response.data.success) {
        const updatedLocations = locations.filter(loc => loc.id !== locationId);
        setLocations(updatedLocations);
        
        // If current location was deleted, switch to first available location
        if (currentLocation && currentLocation.id === locationId) {
          const newCurrentLocation = updatedLocations[0];
          setCurrentLocation(newCurrentLocation);
          localStorage.setItem('currentLocationId', newCurrentLocation.id.toString());
        }
        
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete location');
      }
    } catch (error) {
      alert('Failed to delete location. Please try again.');
      return false;
    }
  };

  // Switch current location
  const switchLocation = (location) => {
    setCurrentLocation(location);
    localStorage.setItem('currentLocationId', location.id.toString());
  };

  useEffect(() => {
    const hasToken = () =>
      !!(localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));

    // Only fetch when authenticated; the provider mounts before login, so a
    // pre-auth fetch would just 401. Re-load when auth becomes available.
    if (hasToken()) {
      loadLocations();
    } else {
      setIsLoading(false);
    }

    const handleAuthChanged = () => {
      if (hasToken()) loadLocations();
    };
    window.addEventListener('auth-changed', handleAuthChanged);
    return () => window.removeEventListener('auth-changed', handleAuthChanged);
  }, []);

  const value = {
    locations,
    currentLocation,
    isLoading,
    addLocation,
    updateLocation,
    deleteLocation,
    switchLocation,
    loadLocations
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};